/**
 * @file PartsCatalogPage.jsx
 * @description The Dingli spare-parts catalogue — the shared engine.
 *
 * Every Dingli model in the fleet, straight from its OEM parts manual: pick a
 * model, walk its sections (Chassis / Turntable / Boom / ...) down to one
 * assembly, and read the exploded drawing beside the parts table that belongs
 * to it — item callout, Dingli part number, description, quantity. Or search a
 * part number across the whole catalogue when you are holding the broken part
 * and need to know what it is and what else uses it.
 *
 * This is REFERENCE data, not stock. Whether we hold a part is the Spare Parts
 * (inventory) page's business; where a row's part number is stocked, the stock
 * on hand is shown against it and links there.
 *
 * Departments mount their own thin wrapper over this file (see
 * pages/service/ServicePartsCatalogPage.jsx), so a departmental UI change is
 * made in the wrapper and never leaks across.
 *
 * Props:
 *   model — preselect this model_number (used when opened from a machine).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    FormControlLabel, IconButton, InputAdornment, Link, MenuItem, Paper,
    Skeleton, Stack, Switch, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Build as PartIcon,
    Inventory2 as StockIcon,
    MenuBook as ManualIcon,
    OpenInNew as OpenIcon,
    Search as SearchIcon,
    ZoomIn as ZoomIcon,
} from '@mui/icons-material';
import partsCatalogService from '../../services/partsCatalogService';

/** A part number the manual leaves blank points at another figure instead. */
const NO_PART = '—';

/**
 * A manual covers a whole model family, and where a part differs between them
 * the Remark column names the models it fits ("BA22CRT2 BA20CRT2"). Read those
 * back out so a row can say whether it is the one for the machine on screen —
 * ordering the wrong variant of a telescope cylinder is an expensive mistake.
 * A remark that names no model at all ("Standard", "Not used") fits everything.
 */
const modelsInRemark = (remark, manualModels) => {
    if (!remark) return [];
    return manualModels.filter(
        (m) => new RegExp(`(^|[^A-Z0-9])${m}([^A-Z0-9]|$)`).test(remark),
    );
};

const PartsCatalogPage = ({ model: forcedModel }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [models, setModels] = useState([]);
    const [modelsError, setModelsError] = useState('');
    const [loadingModels, setLoadingModels] = useState(true);

    const model = forcedModel || searchParams.get('model') || '';
    const figureCode = searchParams.get('figure') || '';
    const mode = searchParams.get('q') ? 'search' : 'browse';

    const [manual, setManual] = useState(null);
    const [loadingManual, setLoadingManual] = useState(false);
    const [manualError, setManualError] = useState('');

    const [figure, setFigure] = useState(null);
    const [loadingFigure, setLoadingFigure] = useState(false);
    const [figureError, setFigureError] = useState('');

    const [term, setTerm] = useState(searchParams.get('q') || '');
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    const [zoomed, setZoomed] = useState(false);
    const [sectionFilter, setSectionFilter] = useState('all');

    // ── the model picker ────────────────────────────────────────────────────
    useEffect(() => {
        let alive = true;
        partsCatalogService.getModels()
            .then((res) => {
                if (!alive) return;
                setModels(res?.data || []);
                setModelsError('');
            })
            .catch(() => alive && setModelsError('Could not load the parts catalogue.'))
            .finally(() => alive && setLoadingModels(false));
        return () => { alive = false; };
    }, []);

    const selected = useMemo(
        () => models.find((m) => m.model_number === model) || null,
        [models, model],
    );
    const fleetModels = useMemo(() => models.filter((m) => m.fleet_count > 0), [models]);
    const manualModels = useMemo(
        () => models.filter((m) => m.manual_key === selected?.manual_key)
            .map((m) => m.model_number),
        [models, selected],
    );
    const otherModels = useMemo(() => models.filter((m) => !m.fleet_count), [models]);

    // Nothing chosen and nothing searched: open on the commonest fleet model.
    useEffect(() => {
        if (forcedModel || model || mode === 'search' || !fleetModels.length) return;
        setSearchParams({ model: fleetModels[0].model_number }, { replace: true });
    }, [forcedModel, model, mode, fleetModels, setSearchParams]);

    // ── the chosen model's manual ───────────────────────────────────────────
    useEffect(() => {
        if (!selected) { setManual(null); return undefined; }
        let alive = true;
        setLoadingManual(true);
        setManualError('');
        partsCatalogService.getManual(selected.manual_key)
            .then((res) => alive && setManual(res?.data || null))
            .catch(() => alive && setManualError('Could not load this manual.'))
            .finally(() => alive && setLoadingManual(false));
        return () => { alive = false; };
    }, [selected]);

    // ── the open assembly ───────────────────────────────────────────────────
    useEffect(() => {
        if (!selected || !figureCode) { setFigure(null); return undefined; }
        let alive = true;
        setLoadingFigure(true);
        setFigureError('');
        setZoomed(false);
        partsCatalogService.getFigure(selected.manual_key, figureCode)
            .then((res) => alive && setFigure(res?.data || null))
            .catch(() => alive && setFigureError('Could not load this assembly.'))
            .finally(() => alive && setLoadingFigure(false));
        return () => { alive = false; };
    }, [selected, figureCode]);

    // ── search ──────────────────────────────────────────────────────────────
    const runSearch = useCallback((q, scopeModel) => {
        if (!q || q.trim().length < 2) return;
        setSearching(true);
        setSearchError('');
        partsCatalogService.search({ q: q.trim(), model: scopeModel || undefined, limit: 100 })
            .then((res) => setResults(res))
            .catch(() => setSearchError('Search failed. Please try again.'))
            .finally(() => setSearching(false));
    }, []);

    const queryParam = searchParams.get('q');
    const scopeParam = searchParams.get('scope');
    useEffect(() => {
        if (queryParam) runSearch(queryParam, scopeParam === 'model' ? model : '');
        else setResults(null);
    }, [queryParam, scopeParam, model, runSearch]);

    const submitSearch = (e) => {
        e.preventDefault();
        if (term.trim().length < 2) return;
        const next = { q: term.trim() };
        if (model) { next.model = model; next.scope = searchParams.get('scope') || 'all'; }
        setSearchParams(next);
    };

    const openFigure = (manualKey, code, forModel) => {
        // A search hit can sit in a manual other than the one on screen; jump
        // to a model that manual covers so the breadcrumb stays truthful.
        const target = forModel
            || (models.find((m) => m.manual_key === manualKey)?.model_number ?? model);
        setSearchParams({ model: target, figure: code });
    };

    const backToBrowse = () => {
        setResults(null);
        setTerm('');
        setSearchParams(model ? { model } : {});
    };

    const sections = manual?.sections || [];
    const visibleSections = sectionFilter === 'all'
        ? sections
        : sections.filter((s) => String(s.section_no) === String(sectionFilter));

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* ── search — no page title, the top bar already names the page ── */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }}>
                <Box component="form" onSubmit={submitSearch} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <TextField
                        size="small"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder="Part number or description"
                        sx={{ minWidth: { xs: '100%', md: 320 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Stack>

            {modelsError && <Alert severity="error" sx={{ mb: 2 }}>{modelsError}</Alert>}

            {/* ── model picker ───────────────────────────────────────────── */}
            {loadingModels ? (
                <Skeleton variant="rectangular" height={70} sx={{ mb: 2, borderRadius: 3 }} />
            ) : (
                !forcedModel && !!models.length && (
                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardContent sx={{ py: 2 }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                alignItems={{ sm: 'center' }}
                            >
                                <TextField
                                    select
                                    size="small"
                                    label="Machine model"
                                    value={model}
                                    onChange={(e) => setSearchParams({ model: e.target.value })}
                                    sx={{ minWidth: 260 }}
                                >
                                    {fleetModels.map((m) => (
                                        <MenuItem key={m.model_number} value={m.model_number}>
                                            {m.model_number}
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                            >
                                                · {m.fleet_count} in fleet
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                    {!!otherModels.length && <Divider />}
                                    {otherModels.map((m) => (
                                        <MenuItem key={m.model_number} value={m.model_number}>
                                            {m.model_number}
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                            >
                                                · not in fleet
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {selected && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        flexWrap="wrap"
                                        useFlexGap
                                    >
                                        <Chip
                                            size="small"
                                            icon={<ManualIcon />}
                                            label={`${selected.doc_no} ${selected.revision}`}
                                        />
                                        <Chip
                                            size="small"
                                            color={selected.fleet_count ? 'success' : 'default'}
                                            label={selected.fleet_count
                                                ? `In fleet · ${selected.fleet_count} machine${selected.fleet_count > 1 ? 's' : ''}`
                                                : 'Not in fleet'}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {selected.figure_count} assemblies ·{' '}
                                            {Number(selected.part_count).toLocaleString('en-IN')} parts
                                        </Typography>
                                        {selected.source_url && (
                                            <Link
                                                href={selected.source_url}
                                                target="_blank"
                                                rel="noopener"
                                                variant="caption"
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                Source manual <OpenIcon sx={{ fontSize: 14 }} />
                                            </Link>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                )
            )}

            {/* ── search results ─────────────────────────────────────────── */}
            {mode === 'search' ? (
                <SearchResults
                    term={queryParam}
                    model={model}
                    scope={scopeParam}
                    results={results}
                    searching={searching}
                    error={searchError}
                    onScope={(s) => setSearchParams({ q: queryParam, model, scope: s })}
                    onOpenFigure={openFigure}
                    onBack={backToBrowse}
                />
            ) : figureCode ? (
                <FigureView
                    figure={figure}
                    loading={loadingFigure}
                    error={figureError}
                    model={model}
                    manualModels={manualModels}
                    zoomed={zoomed}
                    onZoom={() => setZoomed((z) => !z)}
                    onBack={() => setSearchParams({ model })}
                />
            ) : (
                <SectionBrowser
                    manual={manual}
                    loading={loadingManual}
                    error={manualError}
                    sections={visibleSections}
                    allSections={sections}
                    sectionFilter={sectionFilter}
                    onSectionFilter={setSectionFilter}
                    onOpen={(code) => setSearchParams({ model, figure: code })}
                />
            )}
        </Box>
    );
};

/* ── browse: sections → assemblies ────────────────────────────────────────── */
const SectionBrowser = ({
    manual, loading, error, sections, allSections, sectionFilter, onSectionFilter, onOpen,
}) => {
    if (loading) {
        return (
            <Stack spacing={2}>
                {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
                ))}
            </Stack>
        );
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!manual) {
        return (
            <Card sx={{ p: 6, textAlign: 'center' }}>
                <ManualIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} color="text.secondary">
                    Pick a machine model
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Choose a model above to open its parts manual.
                </Typography>
            </Card>
        );
    }

    return (
        <>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={sectionFilter}
                onChange={(_e, v) => v && onSectionFilter(v)}
                sx={{ mb: 2, flexWrap: 'wrap' }}
            >
                <ToggleButton value="all">All sections</ToggleButton>
                {allSections.map((s) => (
                    <ToggleButton key={s.section_no} value={String(s.section_no)}>
                        {s.section_name}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Stack spacing={3}>
                {sections.map((section) => (
                    <Box key={section.section_no}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                            Section {section.section_no} · {section.section_name}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {section.figures.length} assemblies
                            </Typography>
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)',
                                },
                            }}
                        >
                            {section.figures.map((f) => (
                                <Card
                                    key={f.code}
                                    variant="outlined"
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'box-shadow .18s ease, border-color .18s ease',
                                        '&:hover': { boxShadow: 3, borderColor: 'primary.main' },
                                    }}
                                    onClick={() => onOpen(f.code)}
                                >
                                    <Stack direction="row" spacing={1.5} sx={{ p: 1.5 }}>
                                        {f.drawings?.[0] && (
                                            <Box
                                                component="img"
                                                src={f.drawings[0]}
                                                alt=""
                                                loading="lazy"
                                                sx={{
                                                    // contain, not cover: a cropped exploded view
                                                    // loses the very callouts that identify it.
                                                    width: 72, height: 96, objectFit: 'contain',
                                                    borderRadius: 1,
                                                    border: '1px solid', borderColor: 'divider',
                                                    bgcolor: 'background.paper', flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                {f.code}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                                {f.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {f.part_count} parts · manual p.{f.manual_page}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </>
    );
};

/* ── one assembly: drawing + its parts table ──────────────────────────────── */
const FigureView = ({
    figure, loading, error, model, manualModels, zoomed, onZoom, onBack,
}) => {
    const [fitsOnly, setFitsOnly] = useState(false);
    if (loading) return <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!figure) return null;

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Button startIcon={<BackIcon />} onClick={onBack} size="small">
                    All assemblies
                </Button>
                <Divider orientation="vertical" flexItem />
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {figure.code} · {figure.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {model} · Section {figure.section_no} {figure.section_name} ·{' '}
                        {figure.doc_no} {figure.revision} p.{figure.manual_page}
                    </Typography>
                </Box>
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: zoomed ? '1fr' : { xs: '1fr', lg: '5fr 7fr' },
                    alignItems: 'start',
                }}
            >
                <Paper variant="outlined" sx={{ p: 1.5, position: 'relative' }}>
                    <Tooltip title={zoomed ? 'Show the parts table beside it' : 'Enlarge the drawing'}>
                        <IconButton
                            size="small"
                            onClick={onZoom}
                            aria-label={zoomed ? 'Shrink drawing' : 'Enlarge drawing'}
                            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                        >
                            <ZoomIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {figure.drawings?.length ? figure.drawings.map((src) => (
                        <Box
                            key={src}
                            component="img"
                            src={src}
                            alt={`Exploded drawing — ${figure.code} ${figure.title}`}
                            sx={{ width: '100%', display: 'block', mb: 1 }}
                        />
                    )) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                            No drawing printed for this assembly.
                        </Typography>
                    )}
                </Paper>

                <PartsTable
                    items={figure.items || []}
                    model={model}
                    manualModels={manualModels}
                    fitsOnly={fitsOnly}
                    onFitsOnly={setFitsOnly}
                />
            </Box>
        </>
    );
};

const PartsTable = ({ items, model, manualModels = [], fitsOnly, onFitsOnly }) => {
    // Tag each row with the models its remark names, so the ones meant for
    // another machine in the family can be called out rather than silently
    // sitting next to the right one.
    const tagged = items.map((p) => {
        const fits = modelsInRemark(p.remark, manualModels);
        return { ...p, fits, forOther: fits.length > 0 && !fits.includes(model) };
    });
    const variants = tagged.some((p) => p.fits.length);
    const shown = fitsOnly ? tagged.filter((p) => !p.forOther) : tagged;

    if (!items.length) {
        return (
            <Card sx={{ p: 6, textAlign: 'center' }}>
                <PartIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} color="text.secondary">
                    No parts listed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    The manual prints this assembly as a drawing only.
                </Typography>
            </Card>
        );
    }
    return (
        // A Box, not a fragment: this renders into a CSS grid cell, and a
        // fragment would make the toggle and the table two separate cells.
        <Box>
            {variants && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        This assembly has parts that differ between the models the
                        manual covers.
                    </Typography>
                    <FormControlLabel
                        control={(
                            <Switch
                                size="small"
                                checked={!!fitsOnly}
                                onChange={(e) => onFitsOnly(e.target.checked)}
                            />
                        )}
                        label={(
                            <Typography variant="caption">Only what fits {model}</Typography>
                        )}
                    />
                </Stack>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '70vh' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={48}>Item</TableCell>
                            <TableCell width={120}>Part number</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell width={56} align="right">Qty</TableCell>
                            <TableCell width={150}>Fits / remark</TableCell>
                            <TableCell width={110}>In stock</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.map((p) => (
                            <TableRow
                                key={p.seq}
                                hover
                                sx={p.forOther ? { '& td': { color: 'text.disabled' } } : undefined}
                            >
                                <TableCell>
                                    <Typography variant="body2" fontWeight={700}>
                                        {p.item_no ?? '··'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {p.part_number || NO_PART}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{p.description}</Typography>
                                </TableCell>
                                <TableCell align="right">{p.qty ?? ''}</TableCell>
                                <TableCell>
                                    {p.fits.length ? (
                                        <Chip
                                            size="small"
                                            color={p.forOther ? 'default' : 'success'}
                                            variant={p.forOther ? 'outlined' : 'filled'}
                                            label={p.forOther
                                                ? `Fits ${p.fits.join(', ')}`
                                                : `Fits ${model}`}
                                        />
                                    ) : p.remark && (
                                        <Typography variant="caption" color="text.secondary">
                                            {p.remark}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell><StockCell stock={p.stock} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

/**
 * Stock is the inventory module's business — shown here only where the part
 * number is actually carried, so a blank cell reads as "not stocked", not
 * "none left".
 */
const StockCell = ({ stock }) => {
    if (!stock) {
        return <Typography variant="caption" color="text.disabled">Not stocked</Typography>;
    }
    const low = Number(stock.current_stock) <= Number(stock.reorder_level ?? 0);
    return (
        <Chip
            size="small"
            component="a"
            href={`/inventory?search=${encodeURIComponent(stock.part_number)}`}
            clickable
            icon={<StockIcon />}
            color={low ? 'warning' : 'success'}
            label={`${Number(stock.current_stock).toLocaleString('en-IN')} ${stock.unit || 'pcs'}${low ? ' · low' : ''}`}
        />
    );
};

/* ── search across the catalogue ──────────────────────────────────────────── */
const SearchResults = ({
    term, model, scope, results, searching, error, onScope, onOpenFigure, onBack,
}) => (
    <>
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
        >
            <Button startIcon={<BackIcon />} onClick={onBack} size="small">
                Back to the catalogue
            </Button>
            {model && (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={scope === 'model' ? 'model' : 'all'}
                    onChange={(_e, v) => v && onScope(v)}
                >
                    <ToggleButton value="all">All models</ToggleButton>
                    <ToggleButton value="model">{model} only</ToggleButton>
                </ToggleButtonGroup>
            )}
            {results && (
                <Typography variant="body2" color="text.secondary">
                    {Number(results.total).toLocaleString('en-IN')} matches for
                    {' '}&ldquo;{term}&rdquo;
                    {results.total > results.count && ` — showing the first ${results.count}`}
                </Typography>
            )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {searching ? (
            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : results && !results.count ? (
            <Card sx={{ p: 6, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} color="text.secondary">
                    Nothing matches &ldquo;{term}&rdquo;
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Try the Dingli part number, or a single word from the description
                    the manual uses — &ldquo;pump&rdquo; rather than &ldquo;hydraulic pump&rdquo;.
                </Typography>
            </Card>
        ) : results ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '70vh' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={120}>Part number</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Assembly</TableCell>
                            <TableCell width={56} align="right">Qty</TableCell>
                            <TableCell width={110}>In stock</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.data.map((r) => (
                            <TableRow
                                key={`${r.manual_key}-${r.figure_code}-${r.part_number}-${r.description}`}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => onOpenFigure(r.manual_key, r.figure_code)}
                            >
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {r.part_number || NO_PART}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{r.description}</Typography>
                                    {r.remark && (
                                        <Typography variant="caption" color="text.secondary">
                                            {r.remark}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {r.figure_code} · {r.figure_title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {r.section_name} · {r.manual_title}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{r.qty ?? ''}</TableCell>
                                <TableCell><StockCell stock={r.stock} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        ) : null}
    </>
);

export default PartsCatalogPage;
